import { fromZonedTime, toZonedTime, format } from "date-fns-tz";

export class BrazilDate {
  private static readonly TIMEZONE = "America/Sao_Paulo";

  /**
   * Corte do dia operacional de entregas/roteirista (America/Sao_Paulo).
   * De 00:00–02:59 ainda conta como o dia civil anterior.
   */
  static readonly OPERATIONAL_DAY_RESET_HOUR = 3;

  static setupGlobalDate(): void {}

  static now(): Date {
    const now = new Date();
    return toZonedTime(now, this.TIMEZONE);
  }

  static toUTC(date: Date): Date {
    return fromZonedTime(date, this.TIMEZONE);
  }

  static toSaoPaulo(utcDate: Date): Date {
    return toZonedTime(utcDate, this.TIMEZONE);
  }

  static create(
    year: number,
    month: number,
    day: number,
    hour: number = 0,
    minute: number = 0,
    second: number = 0
  ): Date {
    const dateString = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
    return fromZonedTime(dateString, this.TIMEZONE);
  }

  static formatBR(date: Date, includeTime: boolean = true): string {
    const spTime = toZonedTime(date, this.TIMEZONE);

    if (includeTime) {
      return format(spTime, "dd/MM/yyyy HH:mm:ss", { timeZone: this.TIMEZONE });
    }
    return format(spTime, "dd/MM/yyyy", { timeZone: this.TIMEZONE });
  }

  static formatBRNaive(
    date: Date | string | null,
    includeTime: boolean = true
  ): string {
    if (!date) return "-";
    const dateObj = typeof date === "string" ? new Date(date) : date;

    const day = String(dateObj.getUTCDate()).padStart(2, "0");
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
    const year = dateObj.getUTCFullYear();

    if (!includeTime) {
      return `${day}/${month}/${year}`;
    }

    const hours = String(dateObj.getUTCHours()).padStart(2, "0");
    const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");
    const seconds = String(dateObj.getUTCSeconds()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  static formatISO(date: Date): string {
    const spTime = toZonedTime(date, this.TIMEZONE);
    return format(spTime, "yyyy-MM-dd", { timeZone: this.TIMEZONE });
  }

  static parseAsBrazil(dateString: string): Date {
    const normalized = dateString.trim().replace(/\s/, "T").replace(/Z$/i, "");
    const fullDateString = normalized.includes("T")
      ? normalized
      : `${normalized}T00:00:00`;
    return fromZonedTime(fullDateString, this.TIMEZONE);
  }

  static fromISOString(dateString: string): Date {
    const fullDateString = dateString.includes("T")
      ? dateString
      : `${dateString}T00:00:00`;

    return fromZonedTime(fullDateString, this.TIMEZONE);
  }

  static getTimezone(): string {
    return this.TIMEZONE;
  }

  static todayISO(): string {
    return this.formatISO(new Date());
  }

  static subtractCalendarDaysFromISO(isoDate: string, days: number): string {
    const base = this.fromISOString(isoDate);
    const shifted = new Date(base.getTime() - days * 86400000);
    return this.formatISO(shifted);
  }

  static formatDayMonthFromISO(isoDate: string): string {
    const d = this.fromISOString(isoDate);
    const spTime = toZonedTime(d, this.TIMEZONE);
    return format(spTime, "dd/MM", { timeZone: this.TIMEZONE });
  }

  static startOfToday(): Date {
    const n = toZonedTime(new Date(), this.TIMEZONE);
    return this.create(n.getFullYear(), n.getMonth() + 1, n.getDate(), 0, 0, 0);
  }

  private static addCalendarDays(
    year: number,
    month: number,
    day: number,
    deltaDays: number
  ): { year: number; month: number; day: number } {
    const utc = new Date(Date.UTC(year, month - 1, day + deltaDays));
    return {
      year: utc.getUTCFullYear(),
      month: utc.getUTCMonth() + 1,
      day: utc.getUTCDate(),
    };
  }

  /** yyyy-MM-dd do dia operacional vigente (troca às 03:00 SP). */
  static operationalTodayISO(now: Date = new Date()): string {
    const z = toZonedTime(now, this.TIMEZONE);
    let y = z.getFullYear();
    let m = z.getMonth() + 1;
    let d = z.getDate();
    if (z.getHours() < this.OPERATIONAL_DAY_RESET_HOUR) {
      ({ year: y, month: m, day: d } = this.addCalendarDays(y, m, d, -1));
    }
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  static startOfOperationalDay(
    year: number,
    month: number,
    day: number
  ): Date {
    return this.create(
      year,
      month,
      day,
      this.OPERATIONAL_DAY_RESET_HOUR,
      0,
      0
    );
  }

  static endOfOperationalDay(
    year: number,
    month: number,
    day: number
  ): Date {
    const next = this.addCalendarDays(year, month, day, 1);
    return new Date(
      this.startOfOperationalDay(next.year, next.month, next.day).getTime() - 1
    );
  }

  static startOfOperationalToday(now: Date = new Date()): Date {
    const [y, m, d] = this.operationalTodayISO(now).split("-").map(Number);
    return this.startOfOperationalDay(y, m, d);
  }

  static endOfOperationalToday(now: Date = new Date()): Date {
    const [y, m, d] = this.operationalTodayISO(now).split("-").map(Number);
    return this.endOfOperationalDay(y, m, d);
  }
}
